import { inject, Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { OverlayDialogService, UserDataService } from '@services';
import { ErrorViewComponent } from 'app/components/error-view/error-view.component';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';

import { LoggerService } from '../../services/logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class UserDataGuard implements CanActivate {
  private userDataService: UserDataService = inject(UserDataService);
  private overlayDialogService: OverlayDialogService = inject(OverlayDialogService);
  private logger = inject(LoggerService).setContext('UserDataGuard');

  canActivate(): Observable<boolean> {
    return this.userDataService.isUserDataLoaded$.pipe(
      take(1),
      switchMap((isLoaded: boolean) => {
        if (isLoaded) {
          this.logger.info('User data already loaded');
          return of(true);
        } else if (
          !isLoaded &&
          !this.userDataService.userDataExists() &&
          this.userDataService.isUserDataAlreadyLoaded()
        ) {
          /**
           * If getUserData() not ran, but also we have missing user data in session storage,and already
           * set the flag for userDataAlreadyLoaded, it means at least already ran once getUserData()
           * and still is missing user data, so show access-denied error
           */
          this.logger.warn('User data previously loaded but missing - access denied');
          this.overlayDialogService.open(ErrorViewComponent, {
            data: { option: 'access-denied' }
          });
          return of(false);
        } else {
          return this.userDataService.getUserData().pipe(
            map(() => {
              this.logger.info('User data loaded successfully');
              return true;
            }),
            catchError((error: Error) => {
              this.handleUserDataError(error);
              this.logger.error('Error loading user data:', error);
              return of(false);
            })
          );
        }
      })
    );
  }

  private handleUserDataError(error: Error) {
    if (error.message == 'UserNotLoggedIn') {
      // Auth guard will handle redirect to login
      return;
    } else {
      this.logger.warn('Access denied due to error:', error.message);
      this.overlayDialogService.open(ErrorViewComponent, {
        data: { option: 'access-denied' }
      });
    }
  }
}
